import { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useApi } from "../context/ApiContext";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const API_BASE_URL = useApi();

  const tokenRefreshTime = 1000 * 60 * 14; // refresh every 14 mins
  const [authTokens, setAuthTokens] = useState(() =>
    localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null
  );

  const [user, setUser] = useState(() => {
    const tokens = localStorage.getItem("authTokens")
      ? JSON.parse(localStorage.getItem("authTokens"))
      : null;
    return tokens ? jwtDecode(tokens.access) : null;
  });

  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // Login
  const loginUser = async (username, password) => {
    const fetchUrl = `${API_BASE_URL}/api/token/`;
    const response = await fetch(fetchUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json();

    if (response.status === 200) {
      setAuthTokens(data);
      setUser(jwtDecode(data.access));
      localStorage.setItem("authTokens", JSON.stringify(data));
      navigate("/", { replace: true });
    } else {
      alert("Invalid username or password");
    }
  };

  // Logout
  const logOutUser = () => {
    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authTokens");
    navigate("/login");
  };

  // Refresh token
  const updateToken = async () => {
    if (!authTokens?.refresh) {
      setLoading(false);
      return;
    }

    try {
      const fetchUrl = `${API_BASE_URL}/api/token/refresh/`;
      const response = await fetch(fetchUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh: authTokens.refresh }),
      });

      const data = await response.json();

      if (response.status === 200) {
        const newTokens = {
          access: data.access,
          refresh: data.refresh || authTokens.refresh, // preserve refresh
        };
        setAuthTokens(newTokens);
        setUser(jwtDecode(data.access));
        localStorage.setItem("authTokens", JSON.stringify(newTokens));
      } else {
        logOutUser();
      }
    } catch (err) {
      console.error("Token refresh failed:", err);
      logOutUser();
    } finally {
      setLoading(false);
    }
  };

  // Run refresh on load + interval
  useEffect(() => {
    if (loading) updateToken();

    const interval = setInterval(() => {
      if (authTokens) updateToken();
    }, tokenRefreshTime);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens, loading]);

  const contextData = {
    user,
    authTokens,
    loginUser,
    logOutUser,
    loading,
  };

  return (
    <AuthContext.Provider value={contextData}>
      {children}
    </AuthContext.Provider>
  );
};
